import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import { Add, Delete, Edit } from "@mui/icons-material";
import AccessControl from "../../components/commonComponents/Hoc/AccessControl";
import { StyledMainBox, HtmlTooltip } from "../../components/commonComponents/StyledComponents/styled";
import ActionButton from "../../components/commonComponents/Buttons/ActionButton";
import ListingDataTable from "../../components/commonComponents/TabularLists/ListingDataTable";
import {
  ListingCellCenter,
  ListingCellLeft,
  ListingHeaderContent,
} from "../../components/commonComponents/TabularLists/styles";
import Pagination from "../../components/commonComponents/LoadMore/Pagination";
import Confirmation from "../../components/commonComponents/Confirmation";
import TruncatedText from "../../components/commonComponents/Announcement/TruncatedText";
import AnnouncementFormDialog from "./modalContent/AnnouncementFormDialog";
import { checkUserPermissions } from "../../api_calls/userManagement";
import { deleteAnnouncement, fetchAnnouncements } from "../../api_calls/announcementRequests";
import {
  ANNOUNCEMENT_FEED_PAGE_SIZE,
  formatAnnouncementDate,
  formatAnnouncementTypesDisplay,
  formatAnnouncementTypesList,
  publishedByDisplay,
  publishedOnDisplay,
  publishedStatusLabel,
} from "../../api_calls/announcementUtils";
import { SortIcon } from "../../api_calls/utils";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../Store/actions";
import { toast } from "react-toastify";
import { CustomTextInput } from "../../components/commonComponents/TextInputs/style";

const SEARCH_DEBOUNCE_MS = 400;
// ListingDataTable pads every cell with 40px on the left and on the right
const TABLE_CELL_X_PADDING = 80;
const TITLE_MIN_WIDTH = 250;

const Announcements = () => {
  const currentStateData = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;
  // announcement_can_view only grants read access, creating/editing/deleting needs the edit scope
  const canEditAnnouncements = permissionScope.announcement_can_edit === true;
  const [announcements, setAnnouncements] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [formState, setFormState] = React.useState({ open: false, usedFor: "create", announcement: null });
  const [deleteState, setDeleteState] = React.useState({ open: false, announcement: null });
  const [searchVal, setSearchVal] = React.useState({ value: "" });
  const [searchQuery, setSearchQuery] = React.useState("");
  const [sortingMeasure, setSortingMeasure] = React.useState({
    id: "desc",
    title: "asc",
    type: "asc",
    published: "asc",
    published_timestamp: "asc",
    published_by: "asc",
  });

  const paginationStartValue =
    typeof currentStateData.announcements_pagination !== "undefined"
      ? currentStateData.announcements_pagination.announcement_list_start
      : 0;
  const paginationEndValue =
    typeof currentStateData.announcements_pagination !== "undefined"
      ? currentStateData.announcements_pagination.announcement_list_end
      : ANNOUNCEMENT_FEED_PAGE_SIZE;

  const pageSizeValue = Number(paginationEndValue) - Number(paginationStartValue);
  const pageSize = Number.isInteger(pageSizeValue) && pageSizeValue > 0 ? pageSizeValue : ANNOUNCEMENT_FEED_PAGE_SIZE;
  const pageNumber =
    Number.isInteger(Number(paginationStartValue)) && Number(paginationStartValue) >= 0
      ? Math.floor(Number(paginationStartValue) / pageSize)
      : 0;

  const sorting = currentStateData.announcement_sorting_measure;
  const sortBy =
    sorting && typeof sorting.sortField === "string" && sorting.sortField !== "" ? sorting.sortField : "id";
  const sortDir =
    sorting && typeof sorting.sortOrder === "string" && sorting.sortOrder !== "" ? sorting.sortOrder : "desc";

  const mountedRef = React.useRef(true);
  const loadRequestIdRef = React.useRef(0);
  const loadAnnouncements = React.useCallback(async (silent = false) => {
    const requestId = loadRequestIdRef.current + 1;
    loadRequestIdRef.current = requestId;
    if (silent !== true) {
      setLoading(true);
    }
    const result = await fetchAnnouncements(silent === true, {
      page: pageNumber,
      size: pageSize,
      search: searchQuery,
      sort_by: sortBy,
      sort_dir: sortDir,
    });
    if (mountedRef.current !== true || requestId !== loadRequestIdRef.current) {
      return;
    }
    if (silent !== true) {
      setLoading(false);
    }
    if (result.success === true) {
      setAnnouncements(result.announcements);
      setTotalCount(result.totalElements);
    } else if (silent !== true) {
      toast(result.message || "Sorry, something went wrong", { type: "error" });
    }
  }, [pageNumber, pageSize, searchQuery, sortBy, sortDir]);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  // The list is paginated by the backend, so the keyword has to be filtered there
  // as well. Filtering the loaded page here would only ever search 10 of n rows.
  React.useEffect(() => {
    if (searchVal.value === searchQuery) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      // A new keyword means a new result set, so the current page offset is stale.
      dispatchAction(
        updateStateData({
          announcements_pagination: {
            announcement_list_start: 0,
            announcement_list_end: pageSize,
          },
        })
      );
      setSearchQuery(searchVal.value);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [searchVal.value, searchQuery, pageSize, dispatchAction]);

  const pageRows = announcements;

  const handleSortingParamChange = (sortField, sortOrder) => {
    const nextOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortingMeasure((prev) => ({
      ...prev,
      [sortField]: nextOrder,
    }));

    dispatchAction(
      updateStateData({
        announcement_sorting_measure: {
          sortField,
          sortOrder: nextOrder,
        },
        announcements_pagination: {
          announcement_list_start: 0,
          announcement_list_end: pageSize,
        },
      })
    );
  };

  const columns = React.useMemo(() => {
    const listColumns = [
      {
        id: "id",
        header: (
          <ListingHeaderContent
            onClick={() => handleSortingParamChange("id", sortingMeasure.id)}
            style={{ cursor: "pointer" }}
          >
            ID
            <SortIcon direction={sortingMeasure.id} />
          </ListingHeaderContent>
        ),
        headerClassName: "",
        cellClassName: "",
        headerSx: { width: 50, minWidth: 50, maxWidth: 50, overflow: "hidden", whiteSpace: "nowrap" },
        cellSx: { width: 50, minWidth: 50, maxWidth: 50, overflow: "hidden", whiteSpace: "nowrap" },
        render: (row) => <ListingCellLeft>{row.id}</ListingCellLeft>,
      },
      {
        id: "title",
        header: (
          <ListingHeaderContent
            onClick={() => handleSortingParamChange("title", sortingMeasure.title)}
            style={{ cursor: "pointer" }}
          >
            Title
            <SortIcon direction={sortingMeasure.title} />
          </ListingHeaderContent>
        ),
        headerClassName: "",
        cellClassName: "",
        // no width on purpose: the title takes whatever the other columns leave over, tableMinWidth keeps it from collapsing
        headerSx: { overflow: "hidden", whiteSpace: "nowrap" },
        cellSx: { overflow: "hidden", whiteSpace: "nowrap" },
        render: (row) => (
          <ListingCellLeft sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
            <TruncatedText text={row.title} />
          </ListingCellLeft>
        ),
      },
      {
        id: "type",
        header: (
          <ListingHeaderContent
            onClick={() => handleSortingParamChange("type", sortingMeasure.type)}
            style={{ cursor: "pointer" }}
          >
            Types
            <SortIcon direction={sortingMeasure.type} />
          </ListingHeaderContent>
        ),
        headerClassName: "",
        cellClassName: "",
        headerSx: { width: 150, minWidth: 150, maxWidth: 150, overflow: "hidden", whiteSpace: "nowrap" },
        cellSx: { width: 150, minWidth: 150, maxWidth: 150, overflow: "hidden", whiteSpace: "nowrap" },
        render: (row) => (
          <ListingCellLeft sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
            <TruncatedText
              text={formatAnnouncementTypesDisplay(row)}
              tooltipText={formatAnnouncementTypesList(row)}
            />
          </ListingCellLeft>
        ),
      },
      {
        id: "published",
        header: (
          <ListingHeaderContent
            onClick={() => handleSortingParamChange("published", sortingMeasure.published)}
            style={{ cursor: "pointer" }}
          >
            Status
            <SortIcon direction={sortingMeasure.published} />
          </ListingHeaderContent>
        ),
        headerClassName: "",
        cellClassName: "",
        headerSx: { width: 80, minWidth: 80, maxWidth: 80, overflow: "hidden", whiteSpace: "nowrap" },
        cellSx: { width: 80, minWidth: 80, maxWidth: 80, overflow: "hidden", whiteSpace: "nowrap" },
        render: (row) => (
          <ListingCellLeft sx={{ minWidth: 0, overflow: "hidden" }}>
            {publishedStatusLabel(row)}
          </ListingCellLeft>
        ),
      },
      {
        id: "published_timestamp",
        header: (
          <ListingHeaderContent
            onClick={() => handleSortingParamChange("published_timestamp", sortingMeasure.published_timestamp)}
            style={{ cursor: "pointer" }}
          >
            Published on
            <SortIcon direction={sortingMeasure.published_timestamp} />
          </ListingHeaderContent>
        ),
        headerClassName: "",
        cellClassName: "",
        headerSx: { width: 130, minWidth: 130, maxWidth: 130, overflow: "hidden", whiteSpace: "nowrap" },
        cellSx: { width: 130, minWidth: 130, maxWidth: 130, overflow: "hidden", whiteSpace: "nowrap" },
        render: (row) => (
          <ListingCellLeft sx={{ minWidth: 0, overflow: "hidden" }}>
            {publishedOnDisplay(row, formatAnnouncementDate)}
          </ListingCellLeft>
        ),
      },
      {
        id: "published_by",
        header: (
          <ListingHeaderContent
            onClick={() => handleSortingParamChange("published_by", sortingMeasure.published_by)}
            style={{ cursor: "pointer" }}
          >
            Published by
            <SortIcon direction={sortingMeasure.published_by} />
          </ListingHeaderContent>
        ),
        headerClassName: "",
        cellClassName: "",
        headerSx: { width: 150, minWidth: 150, maxWidth: 150, overflow: "hidden", whiteSpace: "nowrap" },
        cellSx: { width: 150, minWidth: 150, maxWidth: 150, overflow: "hidden", whiteSpace: "nowrap" },
        render: (row) => (
          <ListingCellLeft sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
            <TruncatedText text={publishedByDisplay(row)} />
          </ListingCellLeft>
        ),
      },
    ];

    if (canEditAnnouncements !== true) {
      return listColumns;
    }

    return [
      ...listColumns,
      {
        id: "actions",
        header: <ListingHeaderContent>Actions</ListingHeaderContent>,
        headerAlign: "center",
        align: "center",
        headerClassName: "",
        cellClassName: "",
        headerSx: { width: 70, minWidth: 70, maxWidth: 70, whiteSpace: "nowrap" },
        cellSx: { width: 70, minWidth: 70, maxWidth: 70, whiteSpace: "nowrap" },
        render: (row) => (
          <ListingCellCenter>
            <HtmlTooltip title={<Typography variant="body2">Edit</Typography>}>
              <a
                href="#"
                aria-label={"edit announcement " + row.id}
                onClick={(event) => {
                  event.preventDefault();
                  setFormState({ open: true, usedFor: "edit", announcement: row });
                }}
              >
                <Edit sx={{ color: "#AF3241" }} />
              </a>
            </HtmlTooltip>
            <HtmlTooltip title={<Typography variant="body2">Delete</Typography>}>
              <a
                href="#"
                aria-label={"delete announcement " + row.id}
                onClick={(event) => {
                  event.preventDefault();
                  setDeleteState({ open: true, announcement: row });
                }}
              >
                <Delete sx={{ color: "#AF3241" }} />
              </a>
            </HtmlTooltip>
          </ListingCellCenter>
        ),
      },
    ];
  }, [canEditAnnouncements, sortingMeasure]);

  // the table layout is fixed, so the title (the only column without a width) only gets the space the other columns
  // leave over. The min width keeps at least TITLE_MIN_WIDTH for it, smaller screens scroll horizontally instead.
  const tableMinWidth = columns.reduce(
    (sum, column) => sum + (column.headerSx?.width || 0) + TABLE_CELL_X_PADDING,
    TITLE_MIN_WIDTH
  );

  return (
    <StyledMainBox component="main">
      <Box height={30} />
      <h1>Announcements</h1>
      <Box height={8} />
      <Stack direction="row" spacing={2}>
        {canEditAnnouncements === true && (
          <ActionButton
            onClick={(event) => {
              event.preventDefault();
              setFormState({ open: true, usedFor: "create", announcement: null });
            }}
            label="New Announcement"
            icon={<Add />}
            ariaText="New Announcement"
          />
        )}
      </Stack>
      <Box height={20} />
      <Box width={350}>
        <CustomTextInput
          fullWidth
          variant="standard"
          placeholder="Search"
          label="Search"
          autoComplete="off"
          type="text"
          value={searchVal.value}
          onChange={(event) => setSearchVal({ value: event.target.value })}
        />
      </Box>
      <Box height={25} />
      <ListingDataTable
        columns={columns}
        rows={pageRows}
        rowKey={(row) => row.id}
        emptyText="No announcements found"
        loading={loading}
        tableLayout="fixed"
        tableMinWidth={tableMinWidth}
      />
      <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" mt={2} mb={5}>
        <Pagination
          totalCount={totalCount}
          reduxPaginationKeyword="announcements_pagination"
          reduxStartKeyword="announcement_list_start"
          reduxEndKeyword="announcement_list_end"
          useWindowScroll={true}
        />
      </Stack>

      {formState.open === true && (
        <AnnouncementFormDialog
          open={formState.open}
          usedFor={formState.usedFor}
          announcement={formState.announcement}
          onClose={() => setFormState({ open: false, usedFor: "create", announcement: null })}
          onSaved={() => loadAnnouncements(true)}
        />
      )}

      {deleteState.open === true && (
        <Confirmation
          confirmationTitle="Confirmation"
          confirmationMessage="Are you sure you want to delete this announcement?"
          confirmationActionCall={() => {
            deleteAnnouncement(deleteState.announcement.id).then((info) => {
              if (info.success === true) {
                toast("Announcement deleted successfully", { type: "success" });
                setDeleteState({ open: false, announcement: null });
                loadAnnouncements(true);
              } else {
                toast(info.message || "Sorry, something went wrong", { type: "error" });
              }
            });
          }}
          closePopup={() => setDeleteState({ open: false, announcement: null })}
          showConfirmationPopup={deleteState.open}
        />
      )}
    </StyledMainBox>
  );
};

export default AccessControl(Announcements, ["announcement_can_view", "announcement_can_edit"], ["cyreen"]);
